import { IconName } from "@inflow/icons"
import { createFileRoute, Link } from "@tanstack/react-router"
import { GridList, GridListItem } from "react-aria-components"
import { useTranslation } from "react-i18next"

import Icon from "@/globalComponents/atoms/Icon"
import { cn } from "@/utilities/etc"
import {
  useScopedNavigate,
  useScopedTo,
} from "@/utilities/hooks/useScopedRouting"
import { useTheme } from "@/utilities/hooks/useTheme"
import { ScopedPath } from "@/utilities/scopedRoutes"

import styles from "./index.module.css"

export const Route = createFileRoute("/_authenticated/account/")({
  staticData: {
    pageName: "Account",
  },
  component: AccountIndexRouteComponent,
})

interface MenuItem {
  title: string
  iconName: IconName
  to: ScopedPath
}

export function AccountIndexRouteComponent() {
  const { t } = useTranslation(["common"])
  const scopedNavigate = useScopedNavigate()
  const scoped = useScopedTo()
  const { welcome } = useTheme()

  const hasFlexPurchase = welcome?.flowCard.flows.some(
    (flow) => flow.type === "flexPurchase"
  )

  const menuItems: MenuItem[] = [
    {
      title: t("account.personalInfo.label"),
      iconName: "person",
      to: "/account/personal-information",
    },
    {
      title: t("account.vehicles.label"),
      iconName: "car",
      to: "/account/vehicles",
    },
    ...(hasFlexPurchase
      ? ([
          {
            title: t("myPasses.label", { ns: "park" }),
            iconName: "flexPass",
            to: "/account/passes",
          },
        ] as const)
      : []),
    {
      title: t("account.savedPaymentMethods.label"),
      iconName: "creditCard",
      to: "/account/saved-payment-methods",
    },
    {
      title: t("account.updatePassword.label"),
      iconName: "textEntry",
      to: "/account/update-password",
    },
  ]

  return (
    <GridList
      items={menuItems}
      className={styles.linkList}
      selectionBehavior="replace"
      aria-label={t("account.linkListLabel")}
    >
      {(item) => (
        <GridListItem
          id={item.to}
          textValue={item.title}
          onAction={() => scopedNavigate({ to: item.to })}
        >
          <Link
            {...scoped({ to: item.to })}
            className={cn(styles.links, "bold")}
          >
            <div className={styles.linkIcon}>
              <Icon name={item.iconName} />
            </div>
            {item.title}
            <Icon name="chevronRight" />
          </Link>
        </GridListItem>
      )}
    </GridList>
  )
}
